import CommonModalBase from '@/components/common/CommonModal'
import { ConfirmationDialog } from '@/components/common'
import { MTFormField, MT_MULTI_VALUE_DELIMITER } from '@/multi-territory/mt-components/mt-shared/MTFormField'
import type { MTMetadataFormField } from '@/multi-territory/mt-types/mt-metadata'
import { buildRegexErrorMessage } from '@/multi-territory/mt-utils/mt-regex-hint'
import { Box, Button, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

const CommonModal = CommonModalBase as any

interface MTServiceMasterFormProps {
  open: boolean
  onClose: () => void
  title: string
  fields: MTMetadataFormField[]
  onSubmit?: (formData: Record<string, string | number | null>) => Promise<boolean> | void
  /** Pre-populated values for edit mode */
  initialValues?: Record<string, any>
  /** Existing list records for gap detection */
  listData?: Record<string, any>[]
  /** Field keys that are primary keys (from metadata validations.primary = true) */
  primaryFields?: string[]
}

const GAP_WARNING_MSG = 'There is a gap between the End Date and Start Date for which there will be no applicability of service and broad head. Do you want to continue?'

function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null
  // Supports dd/MM/yyyy
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/')
    if (parts.length === 3) return new Date(Date.UTC(+parts[2], +parts[1] - 1, +parts[0]))
  }
  // Supports yyyy-MM-dd (strip time component)
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoMatch) return new Date(Date.UTC(+isoMatch[1], +isoMatch[2] - 1, +isoMatch[3]))
  return null
}

export function MTServiceMasterForm({ open, onClose, title, fields, onSubmit, initialValues, listData = [], primaryFields = [] }: MTServiceMasterFormProps) {
  const [formValues, setFormValues] = useState<Record<string, string>>({})
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [gapWarningOpen, setGapWarningOpen] = useState(false)
  const [pendingPayload, setPendingPayload] = useState<Record<string, string | number | null> | null>(null)

  useEffect(() => {
    if (open) {
      const initial: Record<string, string> = {}
      fields.forEach(f => {
        if (initialValues && initialValues[f.key] !== undefined && initialValues[f.key] !== null) {
          initial[f.key] = String(initialValues[f.key])
        } else {
          initial[f.key] = ''
        }
      })
      setFormValues(initial)
      setFormErrors({})
    }
  }, [open, fields, initialValues])

  // Returns latest end_date + 1 day (dd/MM/yyyy) for the same primary group, or null
  const getSuggestedStartDate = useCallback((currentPayload: Record<string, string>): string | null => {
    if (!primaryFields.length || !listData.length) return null

    const sameGroup = listData.filter(row =>
      primaryFields.every(key => String(row[key] ?? '').trim() === String(currentPayload[key] ?? '').trim())
    )

    const latestEndDate = sameGroup
      .map(row => parseDate(String(row.end_date || '')))
      .filter((d): d is Date => d !== null)
      .reduce<Date | null>((max, d) => (max === null || d.getTime() > max.getTime() ? d : max), null)

    if (!latestEndDate) return null
    const next = new Date(latestEndDate.getTime() + 24 * 60 * 60 * 1000)
    const dd = String(next.getUTCDate()).padStart(2, '0')
    const mm = String(next.getUTCMonth() + 1).padStart(2, '0')
    const yyyy = next.getUTCFullYear()
    return `${dd}/${mm}/${yyyy}`
  }, [primaryFields, listData])

  const handleChange = useCallback((key: string, value: string) => {
    setFormValues(prev => {
      const updated = { ...prev, [key]: value }
      if (primaryFields.includes(key) && !initialValues) {
        const suggested = getSuggestedStartDate(updated)
        if (suggested) updated['start_date'] = suggested
      }
      return updated
    })
    setFormErrors(prev => { if (prev[key]) { const next = { ...prev }; delete next[key]; return next } return prev })
  }, [primaryFields, initialValues, getSuggestedStartDate])

  const handleMultiChange = useCallback((key: string, values: string[]) => {
    handleChange(key, values.join(MT_MULTI_VALUE_DELIMITER))
  }, [handleChange])

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    fields.forEach(field => {
      const value = (formValues[field.key] || '').trim()


      // In edit mode, skip validation for non-editable (read-only/auto-filled) fields
      if (initialValues && !field.editable) return
      // Required check
      if (field.required && !value) {
        errors[field.key] = field.validations.required_message || (field.label + ' is required')
        return
      }

      // Skip further validation if empty and not required
      if (!value) return

      // Number-specific validations (min/max)
      if (field.controlType === 'number') {
        const numVal = parseFloat(value)
        if (isNaN(numVal)) {
          errors[field.key] = field.label + ' must be a valid number'
          return
        }
        if (field.validations.min !== undefined && numVal < field.validations.min) {
          errors[field.key] = field.label + ' must be at least ' + field.validations.min
          return
        }
        if (field.validations.max !== undefined && numVal > field.validations.max) {
          errors[field.key] = field.label + ' must not exceed ' + field.validations.max
          return
        }
      }

      // Max length check (for string values)
      if (field.validations.maxLength && value.length > field.validations.maxLength) {
        errors[field.key] = field.label + ' must not exceed ' + field.validations.maxLength + ' characters'
        return
      }

      // Regex validation - skip for dropdown/multiselect/date/lookup fields (they have fixed valid values)
      if (
        field.validations.regex &&
        field.controlType !== 'dropdown' &&
        field.controlType !== 'multiselect' &&
        field.controlType !== 'date' &&
        !field.validations.lookupTable &&
        !field.options?.length
      ) {
        let pattern: RegExp
        try { pattern = new RegExp(field.validations.regex) } catch { return }
        // In edit mode, skip regex for values unchanged from the original record
        if (initialValues && value === String(initialValues[field.key] ?? '').trim()) return
        if (!pattern.test(value)) {
          errors[field.key] = buildRegexErrorMessage(field.label, field.validations.regex, field.validations.regex_message)
          return
        }
      }
    })
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const buildPayload = (): Record<string, string | number | null> => {
    const typedPayload: Record<string, string | number | null> = {}
    fields.forEach(field => {
      const raw = (formValues[field.key] || '').trim()
      if (!raw) {
        typedPayload[field.key] = field.required ? '' : null
        return
      }
      typedPayload[field.key] = field.controlType === 'number' ? parseFloat(raw) : raw
    })
    return typedPayload
  }

  const hasGap = (payload: Record<string, string | number | null>): boolean => {
    if (!primaryFields.length || !listData.length) return false
    // In edit mode, skip gap check — user is modifying existing record dates
    if (initialValues) return false
    const newStartDate = parseDate(String(payload['start_date'] || ''))
    if (!newStartDate) return false

    const sameGroup = listData.filter(row =>
      primaryFields.every(key => String(row[key] ?? '').trim() === String(payload[key] ?? '').trim())
    )

    // Only check against the LATEST end_date in the group
    const latestEndDate = sameGroup
      .map(row => parseDate(String(row.end_date || '')))
      .filter((d): d is Date => d !== null)
      .reduce<Date | null>((max, d) => (max === null || d.getTime() > max.getTime() ? d : max), null)

    if (!latestEndDate) return false
    const dayAfterLatestEndMs = latestEndDate.getTime() + 24 * 60 * 60 * 1000
    return newStartDate.getTime() > dayAfterLatestEndMs
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    const payload = buildPayload()
    if (hasGap(payload)) {
      setPendingPayload(payload)
      setGapWarningOpen(true)
      return
    }
    await submitPayload(payload)
  }

  const submitPayload = async (payload: Record<string, string | number | null>) => {
    setLoading(true)
    try {
      const success = await onSubmit?.(payload)
      if (success !== false) { handleClose() }
    } finally { setLoading(false) }
  }

  const handleGapProceed = async () => {
    setGapWarningOpen(false)
    if (pendingPayload) await submitPayload(pendingPayload)
    setPendingPayload(null)
  }

  const handleGapCancel = () => {
    setGapWarningOpen(false)
    setPendingPayload(null)
  }

  const handleClose = () => { setFormValues({}); setFormErrors({}); setGapWarningOpen(false); setPendingPayload(null); onClose() }

  const col1Fields = fields.filter(f => f.gridColumn === 1)
  const col2Fields = fields.filter(f => f.gridColumn === 2)
  const maxRows = Math.max(col1Fields.length, col2Fields.length)

  const isEditMode = !!initialValues

  // Resolve a field's disabled state:
  // - end_date stays disabled until start_date is chosen (add + edit)
  // - fields flagged editable:false are locked ONLY in edit mode; on the Add
  //   form they must stay enabled so the user can set them when creating.
  //   Returns false (not undefined) in add mode to override MTFormField's
  //   default `!field.editable` fallback.
  const getFieldDisabled = (field: MTMetadataFormField): boolean => {
    if (field.key === 'end_date') return !formValues['start_date']
    if (isEditMode && !field.editable) return true
    return false
  }

  return (
    <CommonModal open={open} onClose={handleClose} title={title} maxWidth="sm" modalHeight="auto">
      <>
        <Typography variant="body2" sx={{ color: 'var(--forbrand-error, #d12121)', mb: 2, fontSize: '14px' }}>
          (*) Fields are mandatory
        </Typography>
        <Box sx={{ overflowY: 'auto', maxHeight: '60vh', pr: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '24px', rowGap: '20px', mb: 3 }}>
            {Array.from({ length: maxRows }).map((_, rowIdx) => {
              const leftField = col1Fields[rowIdx]
              const rightField = col2Fields[rowIdx]
              return (
                <Box key={rowIdx} sx={{ display: 'contents' }}>
                  <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                    {leftField && (
                      <MTFormField
                        field={leftField}
                        value={formValues[leftField.key] || ''}
                        error={formErrors[leftField.key]}
                        onChange={handleChange}
                        onMultiChange={handleMultiChange}
                        minDate={leftField.key === 'end_date' ? formValues['start_date'] || undefined : undefined}
                        disabled={getFieldDisabled(leftField)}
                      />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                    {rightField && (
                      <MTFormField
                        field={rightField}
                        value={formValues[rightField.key] || ''}
                        error={formErrors[rightField.key]}
                        onChange={handleChange}
                        onMultiChange={handleMultiChange}
                        minDate={rightField.key === 'end_date' ? formValues['start_date'] || undefined : undefined}
                        disabled={getFieldDisabled(rightField)}
                      />
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
          <Button
            variant="contained"
            disabled={loading}
            onClick={handleSubmit}
            sx={{
              backgroundColor: 'var(--forbrand-bg-variant, #ffaa71)',
              color: 'var(--forbrand-brand-varient, #1f0606)',
              textTransform: 'none',
              px: '24px',
              py: '8px',
              fontSize: '16px',
              borderRadius: '0',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#d4762e', boxShadow: 'none' },
              '&:disabled': { backgroundColor: 'var(--bg-disabled, #dfe3e6)' },
            }}
          >
            {loading ? 'Saving...' : (initialValues ? 'Update' : 'Save')}
          </Button>
        </Box>
      </>
      <ConfirmationDialog
        open={gapWarningOpen}
        onClose={handleGapCancel}
        onConfirm={handleGapProceed}
        onCancel={handleGapCancel}
        title={GAP_WARNING_MSG}
        message=" "
        confirmText="Proceed"
        type="warning"
        itemType=""
        showCancelButton
      />
    </CommonModal>
  )
}
